/**
 * videoPreloadService
 *
 * Proactively buffers the first PREVIEW_SECONDS of each video URL as soon as
 * the API returns items with a storage_url — so the first hover plays instantly
 * from the HTTP cache instead of waiting for a cold network request.
 *
 * Best practices applied:
 * - MAX_CONCURRENT cap: never opens more than 2 parallel preload connections,
 *   so the preloading never competes with visible page content.
 * - Queue: extra URLs wait until a slot is free.
 * - `#t=0,N` media fragment: the browser/CDN only buffers the preview window.
 * - Cleanup after `canplay`: the hidden element is detached and its src cleared,
 *   releasing memory while keeping the response in the HTTP cache.
 * - Error guard: a failed preload is silently skipped; the tile will load on
 *   first hover as it did before.
 * - Idempotent: already-preloaded URLs are skipped immediately.
 */

const PREVIEW_SECONDS = 5;
const MAX_CONCURRENT = 2;

/**
 * Shared across this service and useHoverVideoPreview.
 * A URL in this set means the browser HTTP cache already holds its first
 * PREVIEW_SECONDS, so the hook can skip the extra video.load() call.
 */
export const preloadedUrls = new Set<string>();

let activeCount = 0;
const pendingQueue: string[] = [];

function runNext(): void {
  while (activeCount < MAX_CONCURRENT && pendingQueue.length > 0) {
    const url = pendingQueue.shift()!;

    // Another slot may have finished this URL while it was queued
    if (preloadedUrls.has(url)) continue;

    activeCount++;
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'auto';
    video.src = `${url}#t=0,${PREVIEW_SECONDS}`;

    const cleanup = () => {
      activeCount--;
      // Detach + clear so the element can be GC-ed; HTTP cache keeps the bytes
      video.src = '';
      video.load();
      runNext();
    };

    video.addEventListener('canplay', () => {
      preloadedUrls.add(url);
      cleanup();
    }, { once: true });

    video.addEventListener('error', cleanup, { once: true });

    video.load();
  }
}

/**
 * Enqueue a list of storage URLs for background preloading.
 * Safe to call multiple times — duplicates are filtered out.
 */
export function preloadVideoUrls(urls: string[]): void {
  for (const url of urls) {
    if (!preloadedUrls.has(url) && !pendingQueue.includes(url)) {
      pendingQueue.push(url);
    }
  }
  runNext();
}
